/** Helpers for tutorial flow project history sidebar. */

import { isStepComplete } from '@/lib/learningTutorial/pipelineSteps';

const BACKEND_STEP_TO_UI = {
  pass1: 'pass1',
  pass2: 'pass2',
  orchestration_pass2_optimize: 'pass2_optimize',
  pass2_optimize: 'pass2_optimize',
  pass3: 'script',
  build_script: 'script',
  generate_script: 'script',
  generate_timeline: 'timeline',
  generate_visuals: 'timeline',
  narration_audio: 'narration_audio',
  narration_align: 'word_align',
  narration_embed_artifacts: 'artifact_embed',
  preview_narration: 'artifact_embed',
  concept_tree_v3: 'concept_tree_v3',
  knowledge_analysis_v3: 'knowledge_analysis_v3',
  course_structure_v3: 'course_structure_v3',
  slide_architecture_v3: 'slide_architecture_v3',
  curriculum_optimization_v3: 'curriculum_optimization_v3',
  artifact_specification_v3: 'artifact_specification_v3',
  artifact_generation_v3: 'artifact_generation_v3',
  artifact_rendering_v3: 'artifact_rendering_v3',
  artifact_edm_v3: 'artifact_edm_v3',
  artifact_authoring_v3: 'artifact_authoring_v3',
  artifact_studio_v3: 'artifact_studio_v3',
  artifact_layout_engine_v3: 'artifact_layout_engine_v3',
  artifact_svg_renderer_v3: 'artifact_svg_renderer_v3',
  artifact_rrao_v3: 'artifact_rrao_v3',
};

/** Map backend last_pipeline_step to UI step id. */
export function mapBackendPipelineStep(backendStep) {
  const key = (backendStep || '').trim().toLowerCase();
  return BACKEND_STEP_TO_UI[key] || null;
}

export function isTutorialFlow3Project(project) {
  if (!project) return false;
  const mode = (project.pipeline_mode || '').trim();
  const story = (project.story_type || '').trim();
  return mode === 'tutorial_flow_v3' || story === 'tutorial_flow_v3';
}

export function isTutorialFlow4Project(project) {
  if (!project) return false;
  const mode = (project.pipeline_mode || '').trim();
  const story = (project.story_type || '').trim();
  return mode === 'tutorial_flow_v4' || story === 'tutorial_flow_v4';
}

export function isTutorialFlowProject(project) {
  if (!project) return false;
  const mode = (project.pipeline_mode || '').trim();
  const story = (project.story_type || '').trim();
  return (
    mode === 'tutorial_staged'
    || isTutorialFlow3Project(project)
    || isTutorialFlow4Project(project)
    || story === 'tutorial_flow'
    || story === 'tutorial_flow_v3'
    || story === 'tutorial_flow_v4'
    || Boolean(project.orchestration)
    || Boolean(project.concept_tree_v3)
    || Boolean(project.knowledge_analysis_v3)
  );
}

/** Flow 3 pipeline step + status for TutorialFlow3Page. */
export function flow3UiStateFromProject(project) {
  const lastStatus = (project?.last_pipeline_status || '').toLowerCase();
  const lastStep = (project?.last_pipeline_step || '').trim();

  if (lastStatus === 'failed' && lastStep) {
    return { currentStep: lastStep, status: 'error' };
  }

  if (project?.status === 'artifact_rrao_ready' || project?.artifact_rrao_v3) {
    return { currentStep: null, status: 'ready' };
  }
  if (project?.status === 'artifact_svg_renderer_ready' || project?.artifact_svg_renderer_v3) {
    return { currentStep: 'artifact_rrao_v3', status: 'idle' };
  }
  if (project?.status === 'artifact_layout_engine_ready' || project?.artifact_layout_engine_v3) {
    return { currentStep: 'artifact_svg_renderer_v3', status: 'idle' };
  }
  if (project?.status === 'artifact_studio_ready' || project?.artifact_studio_v3) {
    return { currentStep: 'artifact_layout_engine_v3', status: 'idle' };
  }
  if (project?.status === 'artifact_authoring_ready' || project?.artifact_authoring_v3) {
    return { currentStep: 'artifact_studio_v3', status: 'idle' };
  }
  if (project?.status === 'artifact_edm_ready' || project?.artifact_edm_v3) {
    return { currentStep: 'artifact_authoring_v3', status: 'idle' };
  }
  if (project?.status === 'artifact_rendering_ready' || project?.artifact_rendering_v3) {
    return { currentStep: 'artifact_edm_v3', status: 'idle' };
  }
  if (project?.status === 'artifact_generation_ready' || project?.artifact_generation_v3) {
    return { currentStep: 'artifact_rendering_v3', status: 'idle' };
  }
  if (project?.status === 'artifact_specification_ready' || project?.artifact_specification_v3) {
    return { currentStep: 'artifact_generation_v3', status: 'idle' };
  }
  if (project?.status === 'curriculum_optimized' || project?.curriculum_optimization_v3) {
    return { currentStep: 'artifact_specification_v3', status: 'idle' };
  }
  if (project?.status === 'slide_architecture_ready' || project?.slide_architecture_v3) {
    return { currentStep: 'curriculum_optimization_v3', status: 'idle' };
  }
  if (project?.status === 'course_structure_ready' || project?.course_structure_v3) {
    return { currentStep: 'slide_architecture_v3', status: 'idle' };
  }
  if (project?.status === 'knowledge_analysis_ready' || project?.knowledge_analysis_v3) {
    return { currentStep: 'course_structure_v3', status: 'idle' };
  }
  if (project?.status === 'concept_tree_ready' || project?.concept_tree_v3) {
    return { currentStep: 'knowledge_analysis_v3', status: 'idle' };
  }
  return { currentStep: null, status: 'idle' };
}

const FLOW3_STATUS_LABELS = {
  artifact_rrao_ready: 'RRAO ready',
  artifact_svg_renderer_ready: 'SVG renderer ready',
  artifact_layout_engine_ready: 'Layout engine ready',
  artifact_studio_ready: 'Studio ready',
  artifact_authoring_ready: 'Authoring API ready',
  artifact_edm_ready: 'EDMs ready',
  artifact_rendering_ready: 'ARP ready',
  artifact_generation_ready: 'AKOs ready',
  artifact_specification_ready: 'Artifact specs',
  curriculum_optimized: 'Optimized',
  slide_architecture_ready: 'Slides planned',
  course_structure_ready: 'Course structure',
  knowledge_analysis_ready: 'Analysis ready',
  concept_tree_ready: 'Concept tree',
};

export function flow3StatusLabel(project) {
  const fromStatus = FLOW3_STATUS_LABELS[project?.status];
  if (fromStatus) return fromStatus;
  const step = (project?.last_pipeline_step || '').trim();
  if (step === 'artifact_rrao_v3') return 'RRAO ready (5C.6)';
  if (step === 'artifact_svg_renderer_v3') return 'SVG renderer ready (5C.5)';
  if (step === 'artifact_layout_engine_v3') return 'Layout engine ready (5C.4)';
  if (step === 'artifact_studio_v3') return 'Studio ready (5C.3)';
  if (step === 'artifact_authoring_v3') return 'Authoring API ready (5C.2)';
  if (step === 'artifact_edm_v3') return 'EDMs ready (5C.1)';
  if (step === 'artifact_rendering_v3') return 'ARP ready (5B)';
  if (step === 'artifact_generation_v3') return 'AKOs ready (5A)';
  if (step === 'artifact_specification_v3') return 'Artifact specs';
  if (step === 'curriculum_optimization_v3') return 'Optimized';
  if (step === 'slide_architecture_v3') return 'Slides planned';
  if (step === 'course_structure_v3') return 'Course structure';
  if (step === 'knowledge_analysis_v3') return 'Analysis ready';
  if (step === 'concept_tree_v3') return 'Concept tree';
  if (project?.artifact_rrao_v3) return 'RRAO ready (5C.6)';
  if (project?.artifact_svg_renderer_v3) return 'SVG renderer ready (5C.5)';
  if (project?.artifact_layout_engine_v3) return 'Layout engine ready (5C.4)';
  if (project?.artifact_studio_v3) return 'Studio ready (5C.3)';
  if (project?.artifact_authoring_v3) return 'Authoring API ready (5C.2)';
  if (project?.artifact_edm_v3) return 'EDMs ready (5C.1)';
  if (project?.artifact_rendering_v3) return 'ARP ready (5B)';
  if (project?.artifact_generation_v3) return 'AKOs ready (5A)';
  if (project?.artifact_specification_v3) return 'Artifact specs';
  if (project?.curriculum_optimization_v3) return 'Optimized';
  if (project?.slide_architecture_v3) return 'Slides planned';
  if (project?.course_structure_v3) return 'Course structure';
  if (project?.knowledge_analysis_v3) return 'Analysis ready';
  if (project?.concept_tree_v3) return 'Concept tree';
  return project?.status || 'Draft';
}

/** Restore Flow 4 create form from a saved project. */
export function hydrateFlow4FormFromProject(project, setters) {
  const tree = project?.outline_story || project?.concept_tree_v4;
  const intake = project?.intake || {};
  setters.setStoryName(project?.title || tree?.tutorial_name || '');
  setters.setTopic(intake.topic || tree?.topic || '');
}

export function flow4StatusLabel(project) {
  if (project?.outline_story || project?.concept_tree_v4) return 'Outline ready';
  if (project?.status === 'outline_story_ready') return 'Outline ready';
  return project?.status || 'Draft';
}

export function flow3ProjectPath(projectId = null) {
  return projectId ? `/tutorial/flow3/${projectId}` : '/tutorial/flow3';
}

/** Restore Flow 3 create form from a saved project. */
export function hydrateFlow3FormFromProject(project, setters) {
  const tree = project?.concept_tree_v3;
  const intake = project?.intake || {};
  setters.setTutorialName(project?.title || tree?.tutorial_name || '');
  setters.setTopic(intake.topic || tree?.topic || '');
}

export function tutorialPipelineStepFromProject(project) {
  if (!project) return null;
  const orch = project.orchestration || {};
  const phase = project.narration_sync_phase;

  if (!project.orchestration && !project.script && !project.timeline) {
    const mapped = mapBackendPipelineStep(project.last_pipeline_step);
    if (mapped) return mapped;
    if (phase === 'complete') return 'done';
    if (phase === 'words') return 'timeline';
    if (phase === 'audio' || project.narration_audio_url) return 'word_align';
  }

  if (phase === 'complete' || project.rendered_timeline?.trainer_artifacts_embedded) {
    return 'done';
  }
  if (
    project.timeline?.timeline_finalized
    || project.rendered_timeline?.timeline_finalized
    || (project.timeline?.blocks?.length && phase === 'complete')
  ) {
    return 'artifact_embed';
  }
  if (phase === 'words' || project.rendered_timeline?.narration_word_timings?.length) {
    return 'timeline';
  }
  const clipCount = (project.narration_clip_assets || []).length;
  if ((phase === 'audio' || project.narration_audio_url) && clipCount > 0) {
    return 'word_align';
  }
  const pass = Number(orch.pipeline_pass || 0);
  if (pass >= 3 || project.script) return 'narration_audio';
  if (orch.pass_status?.pass2_optimize === 'complete' || orch.pass_history?.pass2_optimize) {
    return 'script';
  }
  if (pass >= 2) return 'pass2_optimize';
  if (pass >= 1) return 'pass2';
  if (project.orchestration) return 'pass1';
  return null;
}

export function tutorialStatusFromProject(project) {
  const step = tutorialPipelineStepFromProject(project);
  if (step === 'done') return 'ready';
  if (step) return 'idle';
  return 'idle';
}

export function formatTutorialDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    }
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

export function tutorialStatusLabel(project) {
  const step = tutorialPipelineStepFromProject(project);
  if (step === 'done') return 'Ready';
  if (step === 'artifact_embed') return 'Artifact embed pending';
  if (step === 'word_align') return 'WhisperX pending';
  if (step === 'narration_audio') return 'Audio pending';
  if (step === 'pass2_optimize') return 'Pass 2 optimize';
  if (step === 'timeline') return 'Timeline ready';
  if (step === 'script') return 'Script ready';
  if (step === 'pass2') return 'Pass 2';
  if (step === 'pass1') return 'Pass 1';
  return project?.status || 'Draft';
}

/** Map stored project state to TutorialFlowPage pipeline UI. */
export function tutorialUiStateFromProject(project) {
  const orch = project?.orchestration || null;
  const lastStatus = (project?.last_pipeline_status || '').toLowerCase();

  if (lastStatus === 'failed') {
    const failedStep = mapBackendPipelineStep(project?.last_pipeline_step)
      || tutorialPipelineStepFromProject(project);
    if (failedStep && failedStep !== 'done') {
      return { currentStep: failedStep, status: 'error' };
    }
  }

  const step = tutorialPipelineStepFromProject(project);
  if (!step) {
    return { currentStep: null, status: 'idle' };
  }
  if (step === 'done') {
    return { currentStep: 'done', status: 'ready' };
  }
  if (!isStepComplete(step, project, orch)) {
    return { currentStep: step, status: 'idle' };
  }
  return { currentStep: step, status: 'idle' };
}

/**
 * Apply project intake fields to tutorial create form setters.
 * @param {object} project
 * @param {Record<string, (v: unknown) => void>} setters
 */
export function hydrateTutorialFormFromProject(project, setters) {
  const intake = project?.intake || {};
  setters.setTitle(project?.title || '');
  setters.setTopic(intake.topic || '');
  setters.setObjective(intake.learning_objective || '');
  setters.setAudience(intake.audience || 'beginners');
  setters.setDuration(Number(intake.duration_minutes) || 8);
  setters.setPrerequisites(intake.prerequisites || '');
  setters.setKeyPoints((intake.key_points || []).join('\n'));
  setters.setIncludeCode(intake.include_code === true);
  setters.setVisualNotes(
    intake.visual_style_notes
    || 'Beginner-first: plain language, diagrams, flowcharts, and real-world analogies per concept.',
  );
  if (intake.language) setters.setLanguage(intake.language);
  if (intake.theme_id) setters.setThemeId(intake.theme_id);
  if (intake.layout_mode) setters.setLayoutMode(intake.layout_mode);
  if (intake.tts_provider) setters.setTtsProvider(intake.tts_provider);
  if (intake.tts_voice_style) setters.setTtsVoiceStyle(intake.tts_voice_style);
  if (intake.tts_voice_id) setters.setTtsVoiceId(intake.tts_voice_id);
  if (intake.tts_locale) setters.setTtsLocale(intake.tts_locale);
  if (intake.visual_provider) setters.setVisualProvider(intake.visual_provider);
  if (typeof intake.generate_visuals === 'boolean') setters.setGenerateVisuals(intake.generate_visuals);
  if (intake.artifact_strategy) setters.setArtifactStrategy(intake.artifact_strategy);
  if (Array.isArray(intake.preferred_artifacts)) setters.setPreferredArtifacts(intake.preferred_artifacts);
  if (intake.min_artifacts_per_topic) setters.setMinArtifactsPerTopic(intake.min_artifacts_per_topic);
}

import {
  readFlowHistoryPanelOpen,
  writeFlowHistoryPanelOpen,
} from '@/components/creationFlows/FlowHistoryPanel';

const PANEL_KEY = 'tutorial';
const FLOW3_PANEL_KEY = 'tutorial_flow3';
const FLOW4_PANEL_KEY = 'tutorial_flow4';

export function readTutorialHistorySidebarOpen() {
  return readFlowHistoryPanelOpen(PANEL_KEY, true);
}

export function writeTutorialHistorySidebarOpen(open) {
  writeFlowHistoryPanelOpen(PANEL_KEY, open);
}

export function readFlow3HistorySidebarOpen() {
  return readFlowHistoryPanelOpen(FLOW3_PANEL_KEY, true);
}

export function writeFlow3HistorySidebarOpen(open) {
  writeFlowHistoryPanelOpen(FLOW3_PANEL_KEY, open);
}

export function readFlow4HistorySidebarOpen() {
  return readFlowHistoryPanelOpen(FLOW4_PANEL_KEY, true);
}

export function writeFlow4HistorySidebarOpen(open) {
  writeFlowHistoryPanelOpen(FLOW4_PANEL_KEY, open);
}
